/**
 * NotificationProvider Component
 *
 * Provides real-time notification updates via SSE.
 * Should be placed high in the component tree (e.g., in App.tsx).
 *
 * @example
 * ```tsx
 * // In App.tsx
 * function App() {
 *   return (
 *     <NotificationProvider>
 *       <Router>
 *         ...
 *       </Router>
 *     </NotificationProvider>
 *   );
 * }
 * ```
 */

import React, { createContext, useContext, useMemo } from 'react';
import { useNotificationSSE } from '../hooks/useNotificationSSE';

// ============================================================================
// Types
// ============================================================================

interface NotificationContextValue {
  /** Whether the SSE connection is established */
  isConnected: boolean;
  /** Manually trigger reconnection */
  reconnect: () => void;
  /** Manually disconnect */
  disconnect: () => void;
}

interface NotificationProviderProps {
  children: React.ReactNode;
  /** Enable debug logging for SSE events */
  debug?: boolean;
  /** Custom SSE endpoint (default: /api/v1/notifications/sse/stream) */
  endpoint?: string;
}

// ============================================================================
// Context
// ============================================================================

const NotificationContext = createContext<NotificationContextValue | null>(null);

// ============================================================================
// Provider
// ============================================================================

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
  debug = false,
  endpoint,
}) => {
  // Use the SSE hook - this establishes the connection
  const { isConnected, reconnect, disconnect } = useNotificationSSE({
    debug,
    endpoint,
  });

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(
    () => ({
      isConnected,
      reconnect,
      disconnect,
    }),
    [isConnected, reconnect, disconnect]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to access notification SSE connection status and controls.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isConnected, reconnect } = useNotificationContext();
 *
 *   return (
 *     <div>
 *       Status: {isConnected ? 'Connected' : 'Disconnected'}
 *       <button onClick={reconnect}>Reconnect</button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useNotificationContext(): NotificationContextValue {
  const context = useContext(NotificationContext);

  if (!context) {
    throw new Error(
      'useNotificationContext must be used within a NotificationProvider'
    );
  }

  return context;
}

export default NotificationProvider;
